/**
 * MobX store for the v2 provisional-forecast flow.
 *
 * Holds three logical pieces of state:
 *   1. The typeahead search (query, suggestions, loading, debounce).
 *   2. The right-side concept-detail sidebar (open/closed, the GFC the
 *      sidebar is tied to, the NDC universe for that GFC, loading).
 *   3. Temp packout NDC creation (creating flag, last error message).
 *
 * Every action that touches an observable runs inside `runInAction` so
 * MobX strict-mode is satisfied (matches the rest of the fdpStore tree).
 */

import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';

import {
  ActiveNDCForGFC,
  PreLaunchConceptSuggestion,
  TemporaryPackoutNDCRow,
  TemporaryPackoutNDCType,
} from 'Interfaces/FDPInterfaces/provisionalForecastV2Interfaces';
import {
  singleConceptInterface,
  singleContractInterface,
} from 'Interfaces/FDPInterfaces/tmsInterfaces';
import { provisionalForecastV2Services } from 'services/fdp/provisionalForecastV2Services';
import {
  RecentConceptEntry,
  clearRecentConcepts,
  getRecentConcepts,
  pushRecentConcept,
} from 'utils/recentConceptStorage';

export class ProvisionalForecastV2Store {
  // --- 1. Typeahead search ------------------------------------------------- //
  searchQuery: string = '';
  suggestions: PreLaunchConceptSuggestion[] = [];
  isSearching: boolean = false;

  // --- 2. Concept detail sidebar ------------------------------------------- //
  isSidebarOpen: boolean = false;
  selectedConcept: PreLaunchConceptSuggestion | null = null;
  activeNDCs: ActiveNDCForGFC[] = [];
  temporaryNDCs: TemporaryPackoutNDCRow[] = [];
  // Concept-level forecast bucket for the open sidebar. Set when
  // get_all_ndcs_for_gfc resolves; null while loading and for concepts
  // the backend couldn't classify (no recognised ContractType yet).
  conceptType: 'MAS' | 'NC' | null = null;
  isLoadingNDCs: boolean = false;
  // NDCs the user has ticked in the sidebar; values may be either a real
  // NDC11 (string) or a temp NDC's TempNDC string. Order is preserved.
  selectedNDCsForEdit: string[] = [];

  // --- 3. Temp packout NDC creation ---------------------------------------- //
  isCreatingTempNDC: boolean = false;
  createTempNDCError: string | null = null;
  // Inline success banner — the TempNDC string we just created, so the
  // sidebar can show "Created <TempNDC>" instead of a global toast.
  // Cleared on sidebar close and on store reset.
  lastCreatedTempNDC: string | null = null;

  // --- 4. Concept details panel (Edit screen ROW 1) ------------------------ //
  // Persisted on the long-lived store so the Edit screen can render
  // skeletons from frame zero (set isConceptLoading=true *synchronously*
  // when the user clicks "Edit", before the network call resolves).
  // Cleared each time `resetAndFetchConcept` runs so the prior concept's
  // data never flashes through.
  conceptDetails: singleConceptInterface | null = null;
  contractDetails: singleContractInterface[] = [];
  isConceptLoading: boolean = false;

  // --- 5. Recent-concept history ------------------------------------------- //
  // Persisted via localStorage (per-user) so the user can re-open
  // recently-worked concepts without retyping. Loaded once on mount
  // via loadRecentConcepts(email).
  //
  // Important UX note on order: when a user re-clicks a recent item,
  // localStorage IS updated right away (so a refresh shows the new
  // order), but the in-memory `recentConcepts` list is NOT — that
  // would cause the cards to visibly shift under the user's cursor
  // mid-click, which feels janky. Instead we set
  // `recentNeedsResync = true` and reload from storage at the next
  // safe moment: when the user starts typing again (their attention
  // is on the search field, not the cards) or on a fresh mount.
  recentConcepts: RecentConceptEntry[] = [];
  showAllRecent: boolean = false;
  recentNeedsResync: boolean = false;

  // --- internal ------------------------------------------------------------ //
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private searchAbortController: AbortController | null = null;
  // Cancels the in-flight `get_all_ndcs_for_gfc` request from a previous
  // concept selection so its late response can't overwrite the
  // freshly-selected concept's data in the sidebar.
  private selectConceptAbortController: AbortController | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // ------------------------------------------------------------------------ //
  // Search                                                                   //
  // ------------------------------------------------------------------------ //
  setSearchQuery = (query: string) => {
    this.searchQuery = query;
    this.scheduleSearch(query);
  };

  /**
   * Debounced typeahead. The backend latency on the remote SQL Server
   * is ~250-500ms per non-empty query (see CONCEPT_SUGGESTIONS_API
   * doc), so a 300ms debounce keeps the UI from spamming on every keystroke.
   */
  private scheduleSearch = (query: string) => {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.runSearch(query);
    }, 300);
  };

  runSearch = async (query: string) => {
    // Abort any previous in-flight typeahead so a stale response can't
    // overwrite the user's newer query. Reuses the same controller field
    // as `fetchSuggestions` because the two share semantics — only the
    // most recent suggestion-fetch should be allowed to resolve.
    if (this.searchAbortController) {
      this.searchAbortController.abort();
    }
    const controller = new AbortController();
    this.searchAbortController = controller;
    runInAction(() => {
      this.isSearching = true;
    });
    try {
      const data = await provisionalForecastV2Services.getPreLaunchConceptSuggestions(
        query,
        controller.signal
      );
      if (controller.signal.aborted) return;
      runInAction(() => {
        this.suggestions = data;
      });
    } catch (err) {
      const errName = (err as { name?: string } | null)?.name;
      // Swallow cancellations silently — they're an expected part of
      // the abort flow, not a user-visible failure.
      if (errName === 'CanceledError' || errName === 'AbortError') return;
      console.error('ProvisionalForecastV2Store.runSearch error', err);
      runInAction(() => {
        this.suggestions = [];
      });
    } finally {
      if (!controller.signal.aborted) {
        runInAction(() => {
          this.isSearching = false;
        });
      }
    }
  };

  /**
   * Pre-load the empty-query default when the search field first mounts
   * so the user sees something the moment they focus the input. Cheap
   * — backend caches this list.
   */
  preloadDefaultSuggestions = async () => {
    if (this.suggestions.length > 0) return;
    await this.runSearch('');
  };

  /**
   * Promise-returning suggestion fetcher for use by V2AsyncSearchInput's
   * `loadOptions` prop. Keeps the wiki-style component generic by NOT
   * coupling it to MobX observation — the dropdown just awaits this
   * function. Errors are swallowed (the component renders "No results"
   * when an empty array comes back), and a fresh AbortController per
   * call cancels the previous request so out-of-order responses can't
   * overwrite a newer query's results.
   */
  fetchSuggestions = async (query: string): Promise<PreLaunchConceptSuggestion[]> => {
    if (this.searchAbortController) {
      this.searchAbortController.abort();
    }
    const controller = new AbortController();
    this.searchAbortController = controller;
    try {
      const data = await provisionalForecastV2Services.getPreLaunchConceptSuggestions(
        query,
        controller.signal
      );
      // If a newer call has aborted us, drop this result on the floor.
      if (controller.signal.aborted) return [];
      return data;
    } catch (err) {
      // Silently absorb cancellations; surface real errors to the console.
      const errName = (err as { name?: string } | null)?.name;
      if (errName !== 'CanceledError' && errName !== 'AbortError') {
        console.error('ProvisionalForecastV2Store.fetchSuggestions error', err);
      }
      return [];
    }
  };

  // ------------------------------------------------------------------------ //
  // Concept selection -> open sidebar                                        //
  // ------------------------------------------------------------------------ //
  /**
   * Open the sidebar for `concept` and fetch its NDC universe.
   *
   * `userEmail` is forwarded straight through to the recent-concept
   * cache. We accept it as a parameter rather than reaching into
   * `UserStore` from here — keeps this store self-contained and the
   * caller (the search component) is already pulling the email from
   * `useStore()` for navbar rendering, so there's no extra plumbing.
   */
  selectConceptAndOpenSidebar = async (
    concept: PreLaunchConceptSuggestion,
    userEmail: string | null | undefined
  ) => {
    // Cancel the previous concept's in-flight NDC load (if any) so its
    // late response can't overwrite this concept's data. Bug repro:
    // click concept A → sidebar starts loading A → click concept B
    // before A resolves → A's response lands and stomps B.
    if (this.selectConceptAbortController) {
      this.selectConceptAbortController.abort();
    }
    const controller = new AbortController();
    this.selectConceptAbortController = controller;

    runInAction(() => {
      this.selectedConcept = concept;
      this.isSidebarOpen = true;
      this.activeNDCs = [];
      this.temporaryNDCs = [];
      this.conceptType = null;
      this.selectedNDCsForEdit = [];
      this.isLoadingNDCs = true;
      this.createTempNDCError = null;
    });

    // Persist to storage immediately so a refresh / new tab sees the
    // new order, BUT don't push the new ordering into `recentConcepts`
    // yet — that would visibly reshuffle the cards under the user's
    // cursor right after they click. Instead we mark the list as
    // needing a resync; the search component triggers the resync
    // when the user starts typing (their gaze is on the input, the
    // shuffle goes unnoticed).
    pushRecentConcept(userEmail, concept);
    runInAction(() => {
      this.recentNeedsResync = true;
    });

    try {
      const data = await provisionalForecastV2Services.getAllNDCsForGFC(
        concept.GFC,
        controller.signal
      );
      // Drop on the floor if a newer selection has aborted us — without
      // this guard the late response would still hit runInAction and
      // overwrite the freshly-selected concept's data.
      if (controller.signal.aborted) return;
      runInAction(() => {
        this.activeNDCs = data.active_ndcs ?? [];
        this.temporaryNDCs = data.temporary_ndcs ?? [];
        this.conceptType = data.concept_type ?? null;
      });
    } catch (err) {
      // Swallow cancellations silently — they're an expected part of
      // the abort flow, not a user-visible failure.
      const errName = (err as { name?: string } | null)?.name;
      if (errName === 'CanceledError' || errName === 'AbortError') return;
      console.error('ProvisionalForecastV2Store.selectConceptAndOpenSidebar error', err);
      toast.error('Could not load NDCs for the selected concept.');
    } finally {
      // Only clear the loading flag if this call is still the
      // authoritative one. Otherwise the newer selection's loader has
      // already taken over and we shouldn't flicker it off.
      if (!controller.signal.aborted) {
        runInAction(() => {
          this.isLoadingNDCs = false;
        });
      }
    }
  };

  // ------------------------------------------------------------------------ //
  // Recent concepts                                                          //
  // ------------------------------------------------------------------------ //
  /** Hydrate the recent list from localStorage. Idempotent — safe to
   *  call on every mount. Also clears the `recentNeedsResync` flag
   *  since the in-memory list now matches storage. */
  loadRecentConcepts = (userEmail: string | null | undefined) => {
    const list = getRecentConcepts(userEmail);
    runInAction(() => {
      this.recentConcepts = list;
      this.recentNeedsResync = false;
    });
  };

  /**
   * If a previous `selectConceptAndOpenSidebar` deferred its visual
   * reorder, refresh the in-memory list from storage now. No-op when
   * nothing is pending. Designed to be called from the search input
   * the moment the user starts interacting (focus / first keystroke):
   * the user's attention is on the search field, so the cards below
   * can shift without being noticed.
   */
  resyncRecentIfNeeded = (userEmail: string | null | undefined) => {
    if (!this.recentNeedsResync) return;
    const list = getRecentConcepts(userEmail);
    runInAction(() => {
      this.recentConcepts = list;
      this.recentNeedsResync = false;
    });
  };

  clearRecent = (userEmail: string | null | undefined) => {
    clearRecentConcepts(userEmail);
    runInAction(() => {
      this.recentConcepts = [];
      this.showAllRecent = false;
      this.recentNeedsResync = false;
    });
  };

  toggleShowAllRecent = () => {
    runInAction(() => {
      this.showAllRecent = !this.showAllRecent;
    });
  };

  closeSidebar = () => {
    // Cancel any in-flight concept load so its response can't land in
    // the sidebar after the user has dismissed it.
    if (this.selectConceptAbortController) {
      this.selectConceptAbortController.abort();
      this.selectConceptAbortController = null;
    }
    runInAction(() => {
      this.isSidebarOpen = false;
      this.lastCreatedTempNDC = null;
      this.createTempNDCError = null;
    });
  };

  // Lets the sidebar dismiss the inline success banner without
  // closing the whole panel.
  dismissLastCreatedTempNDC = () => {
    runInAction(() => {
      this.lastCreatedTempNDC = null;
    });
  };

  toggleNDCSelection = (ndcValue: string) => {
    runInAction(() => {
      const idx = this.selectedNDCsForEdit.indexOf(ndcValue);
      if (idx === -1) {
        this.selectedNDCsForEdit = [...this.selectedNDCsForEdit, ndcValue];
      } else {
        this.selectedNDCsForEdit = this.selectedNDCsForEdit.filter((_, i) => i !== idx);
      }
    });
  };

  // ------------------------------------------------------------------------ //
  // Concept details (Edit screen ROW 1)                                      //
  // ------------------------------------------------------------------------ //
  /**
   * Clear the concept observables synchronously, then fetch the
   * concept + contract details for `gfc_id` via the v2
   * `get_concept_details_for_gfc` endpoint.
   *
   * Clearing happens in the same tick the caller invokes us, which is
   * what lets the Edit screen render its skeleton from frame zero — by
   * the time the next paint happens, `conceptDetails` is already null
   * and `isConceptLoading` is already true. Without that, on a second
   * visit the previous concept's data would flash through for one frame
   * before the loading state took over.
   */
  resetAndFetchConcept = async (gfc_id: number) => {
    runInAction(() => {
      this.conceptDetails = null;
      this.contractDetails = [];
      this.isConceptLoading = true;
    });

    try {
      const data = await provisionalForecastV2Services.getPreLaunchConceptDetails(gfc_id);
      runInAction(() => {
        this.conceptDetails = data.concept_info[0] ?? null;
        this.contractDetails = data.contract_info ?? [];
      });
    } catch (error) {
      console.error('resetAndFetchConcept: concept fetch failed', error);
    } finally {
      runInAction(() => {
        this.isConceptLoading = false;
      });
    }
  };

  // ------------------------------------------------------------------------ //
  // Temp packout NDC creation                                                //
  // ------------------------------------------------------------------------ //
  createTempNDC = async (
    gfc: number,
    type: TemporaryPackoutNDCType
  ): Promise<TemporaryPackoutNDCRow | null> => {
    runInAction(() => {
      this.isCreatingTempNDC = true;
      this.createTempNDCError = null;
    });
    try {
      const row = await provisionalForecastV2Services.createTemporaryPackoutNDC({
        gfc,
        type,
      });
      runInAction(() => {
        // Push the freshly-created row into the sidebar list so the UI
        // updates without a refetch round-trip.
        this.temporaryNDCs = [row, ...this.temporaryNDCs];
        // Auto-select it for the user's convenience.
        if (!this.selectedNDCsForEdit.includes(row.TempNDC)) {
          this.selectedNDCsForEdit = [...this.selectedNDCsForEdit, row.TempNDC];
        }
        // Inline confirmation in the sidebar — replaces the prior
        // global toast so the user sees feedback in the same panel
        // where the action ran.
        this.lastCreatedTempNDC = row.TempNDC;
      });
      return row;
    } catch (err) {
      // The backend wraps validation/state errors as { error: string }.
      // axios surfaces them on err.response.data.
      const message = this.extractErrorMessage(err) ?? 'Could not create temporary NDC.';
      runInAction(() => {
        this.createTempNDCError = message;
      });
      toast.error(message);
      return null;
    } finally {
      runInAction(() => {
        this.isCreatingTempNDC = false;
      });
    }
  };

  private extractErrorMessage = (err: unknown): string | null => {
    if (err && typeof err === 'object' && 'response' in err) {
      const resp = (err as { response?: { data?: { error?: string } } }).response;
      if (resp?.data?.error) return resp.data.error;
    }
    return null;
  };

  // ------------------------------------------------------------------------ //
  // Cleanup                                                                  //
  // ------------------------------------------------------------------------ //
  resetStore = () => {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
    if (this.searchAbortController) {
      this.searchAbortController.abort();
      this.searchAbortController = null;
    }
    if (this.selectConceptAbortController) {
      this.selectConceptAbortController.abort();
      this.selectConceptAbortController = null;
    }
    runInAction(() => {
      this.searchQuery = '';
      this.suggestions = [];
      this.isSearching = false;
      this.isSidebarOpen = false;
      this.selectedConcept = null;
      this.activeNDCs = [];
      this.temporaryNDCs = [];
      this.isLoadingNDCs = false;
      this.selectedNDCsForEdit = [];
      this.isCreatingTempNDC = false;
      this.createTempNDCError = null;
      this.lastCreatedTempNDC = null;
      this.conceptDetails = null;
      this.contractDetails = [];
      this.isConceptLoading = false;
    });
  };
}
